import _ from 'lodash';
import { useMemo } from 'react';
import { DEBOUNCE, type DebounceValue, SHOULD_REDUCE_DEBOUNCE_TIME } from '@/core/core.constants';
import { useCurrentValueRef } from '@/core/hooks/useCurrentValueRef.hook';
import type { DebounceSettings } from 'lodash';

/**
 * This hook wraps lodash's debounce function, so that we can use debounce in functional components. The reason we
 * can't use debounce directly is that we need to make sure that the callback we're calling is the current value of the
 * callback function. See more info about the general issue here:
 * https://overreacted.io/making-setinterval-declarative-with-react-hooks/.
 *
 * @param callback - the callback to debounce
 * @param wait - the number of milliseconds to wait before making another call to callback
 * @param options - options passed through to lodash's debounce
 * @return the debounced callback
 */
export function useDebounce(callback: (...args: any[]) => any, wait: DebounceValue, options: DebounceSettings = {}) {
  const { leading = false, trailing = true, maxWait } = options;
  const callbackRef = useCurrentValueRef(callback);
  const effectiveWait = SHOULD_REDUCE_DEBOUNCE_TIME ? DEBOUNCE.SHORT : wait;
  return useMemo(
    () =>
      _.debounce(
        (...args: any[]) => callbackRef.current(...args),
        effectiveWait,
        // lodash treats an undefined maxWait key as maxWait = wait, effectively throttling:
        // https://github.com/lodash/lodash/issues/5857
        maxWait === undefined ? { leading, trailing } : { leading, trailing, maxWait },
      ),
    [effectiveWait, leading, trailing, maxWait],
  );
}
